<template>
  <div class="page__inventory-list">
    <h1 class="h3 mb-4 d-flex align-items-center">
      <a-button
        type="link"
        size="small"
        class="mr-2 p-0"
        style="color: inherit"
        @click="goBackToIndex"
      >
        <a-icon type="left" style="font-size: 20px" />
      </a-button>
      {{ $t('reservedStock.bulk_edit_reserved_stock') }}
    </h1>


    <a-tabs v-model="activeTab" size="large" class="pca-tab-store mt-4" :animated="{ inkBar: true, tabPane: false }">
      <a-tab-pane key="upload" :tab="$t('reservedStock.upload_tab')">
        <a-card :bordered="false" class="border-top">
          <div style="max-width: 600px">
            <div class="mb-4">
              <h6 class="font-weight-bold mb-1">
                {{ $t('reservedStock.download_template_title') }}
              </h6>
              <p class="text-muted small mb-2">
                {{ $t('reservedStock.download_template_desc') }}
              </p>
              <a-button
                type="primary"
                class="btn-pink d-flex align-items-center"
                @click="downloadTemplate"
              >
                <a-icon type="download" class="mr-1" />
                {{ $t('reservedStock.download_template') }}
              </a-button>
            </div>

            <div class="mb-2">
              <h6 class="font-weight-bold mb-1">
                {{ $t('reservedStock.upload_file_title') }}
              </h6>
              <p class="text-muted small mb-3">
                {{ $t('reservedStock.upload_excel_limit_hint') }}
              </p>
            </div>

            <a-upload-dragger
              name="file"
              :multiple="false"
              :show-upload-list="false"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              :before-upload="beforeUpload"
              :disabled="isUploading || processStatus === 'ONPROGRESS'"
            >
              <p class="ant-upload-drag-icon">
                <a-icon type="inbox" />
              </p>
              <p class="ant-upload-text">
                {{ $t('reservedStock.upload_excel_hint') }}
              </p>
              <p class="ant-upload-hint">
                {{ $t('reservedStock.upload_excel_limit_hint') }}
              </p>
            </a-upload-dragger>

            <div
              v-if="file"
              class="mt-3 d-flex align-items-center justify-content-between p-2 border rounded"
            >
              <div class="d-flex align-items-center">
                <a-icon type="file-excel" class="mr-2" style="color: #1890ff" />
                <span>{{ file.name }}</span>
              </div>
              <a-icon
                v-if="!isUploading && processStatus !== 'ONPROGRESS'"
                type="close"
                class="cursor-pointer text-danger"
                @click="removeFile"
              />
            </div>

            <a-button
              v-if="file"
              type="primary"
              class="mt-4 w-100"
              size="large"
              :loading="isUploading || processStatus === 'ONPROGRESS'"
              :disabled="!!processResult && processStatus !== 'FAILED'"
              @click="handleUpload"
            >
              {{
                isUploading || processStatus === 'ONPROGRESS'
                  ? $t('reservedStock.processing')
                  : $t('reservedStock.start_import')
              }}
            </a-button>
          </div>

          <!-- Result Summary -->
          <div v-if="processResult" class="mt-5 pt-4 border-top">
            <div
              class="p-4 rounded mb-4"
              :style="{
                backgroundColor: processResult.status === 'FAILED' ? '#fff1f0' : '#f6ffed',
                border: processResult.status === 'FAILED' ? '1px solid #ffa39e' : '1px solid #b7eb8f',
              }"
            >
              <div class="d-flex">
                <a-icon
                  :type="processResult.status === 'FAILED' ? 'close-circle' : 'check-circle'"
                  theme="filled"
                  :style="{
                    fontSize: '24px',
                    color: processResult.status === 'FAILED' ? '#f5222d' : '#52c41a',
                    marginRight: '16px',
                    marginTop: '2px',
                  }"
                />
                <div class="flex-grow-1">
                  <h4
                    class="mb-3 font-weight-bold"
                    :style="{ color: processResult.status === 'FAILED' ? '#cf1322' : '#389e0d' }"
                  >
                    {{ processResult.status === 'FAILED' ? $t('reservedStock.import_failed') : $t('reservedStock.import_successful') }}
                  </h4>
                  
                  <div class="d-flex flex-wrap mb-4" style="gap: 32px">
                    <div>
                      <div class="text-muted small">
                        {{ $t('reservedStock.total_rows') }}
                      </div>
                      <div class="font-weight-bold" style="font-size: 18px">
                        {{ processResult.total_rows || 0 }}
                      </div>
                    </div>
                    <div>
                      <div class="text-muted small">
                        {{ $t('reservedStock.success_count') }}
                      </div>
                      <div class="font-weight-bold" style="font-size: 18px; color: #52c41a">
                        {{ processResult.success_count || 0 }}
                      </div>
                    </div>
                    <div>
                      <div class="text-muted small">
                        {{ $t('reservedStock.failed_count') }}
                      </div>
                      <div class="font-weight-bold" style="font-size: 18px; color: #f5222d">
                        {{ processResult.failed_count || 0 }}
                      </div>
                    </div>
                  </div>

                  <a-button
                    v-if="processResult.process_id"
                    type="primary"
                    icon="download"
                    @click="downloadFile(processResult.process_id)"
                  >
                    {{ $t('reservedStock.download_result_file') }}
                  </a-button>
                </div>
              </div>
            </div>

            <!-- Error Details Table -->
            <div v-if="processResult.errors && processResult.errors.length">
              <h6 class="text-danger mt-4 mb-3 font-weight-bold">
                <a-icon type="warning" theme="filled" /> {{ $t('reservedStock.error_details_count', { count: processResult.errors.length }) }}
              </h6>
              <a-table
                :columns="errorColumns"
                :data-source="processResult.errors"
                :pagination="{ pageSize: 10 }"
                size="middle"
                row-key="rowNumber"
                class="border rounded"
              >
                <template #errorMessage="text">
                  <div class="py-1" style="word-break: break-word">
                    {{ text }}
                  </div>
                </template>
              </a-table>
            </div>
          </div>
        </a-card>
      </a-tab-pane>

      <a-tab-pane key="history" :tab="$t('reservedStock.history_tab')">
        <div class="p-3 bg-white border-top">
          <a-row :gutter="16" type="flex">
            <a-col :xs="24" :sm="16" :md="12">
              <div class="d-flex align-items-center">
                <span class="mr-3 text-nowrap">{{ $t('utils.search') }}</span>
                <a-input-search
                  v-model="searchText"
                  allow-clear
                  size="large"
                  class="w-100"
                  :enter-button="$t('utils.search')"
                  :placeholder="$t('reservedStock.search_placeholder') || 'SKU / Nama Produk'"
                  @search="onSearch"
                />
              </div>
            </a-col>
          </a-row>
        </div>

        <div class="history-table-wrapper">
          <table class="history-list-table">
            <thead>
              <tr>
                <th style="min-width: 300px">
                  {{ $t('reservedStock.history_table_col_info') }}
                </th>
                <th style="min-width: 250px">
                  {{ $t('reservedStock.history_table_col_detail') }}
                </th>
                <th style="width: 120px" class="text-right">
                  {{ $t('reservedStock.history_table_col_action') }}
                </th>
              </tr>
            </thead>
            
            <template v-if="!isHistoryLoading && historyData.length">
              <HistoryListItem
                v-for="record in historyData"
                :key="record.process_id"
                :item="record"
                :search-text="searchText"
                :warehouse-hash="warehouseHash"
                @download="downloadFile"
                @view-detail="openDetailDrawer"
              />
            </template>
            
            <tbody v-else-if="isHistoryLoading">
              <tr>
                <td colspan="5" class="py-5 text-center">
                  <a-spin />
                  <div class="mt-2 text-muted">
                    {{ $t('reservedStock.loading_history') }}
                  </div>
                </td>
              </tr>
            </tbody>
            
            <tbody v-else>
              <tr>
                <td colspan="5" class="py-5 text-center">
                  <a-empty />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </a-tab-pane>
    </a-tabs>

    <div v-if="activeTab === 'history'" class="mt-4 text-right">
      <a-pagination
        :current="pagination.current"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :show-total="(total, range) => $t('utils.pagination_show_total', { perpage: `${range[0]} - ${range[1]}`, total: total })"
        show-size-changer
        @change="handleTableChangePagination"
        @showSizeChange="handleSizeChange"
      />
    </div>

    <DrawerReservedStockBatchDetail
      :visible="drawerVisible"
      :batch="selectedBatch"
      :warehouse-hash="warehouseHash"
      @close="drawerVisible = false"
    />
  </div>
</template>

<script>
import HistoryListItem from './HistoryListItem.vue'
import DrawerReservedStockBatchDetail from './DrawerReservedStockBatchDetail.vue'
import { 
  downloadReservedStockTemplate, 
  uploadReservedStock,
  checkUploadStatus,
  getReservedStockUploadHistory,
  downloadAnnotatedResult,
} from '@/api/reserved-stock'
import { getHashWarehouseList } from '@/api/warehouse'
import dayjs from 'dayjs'

export default {
  name: 'ImportReservedStock',
  components: {
    HistoryListItem,
    DrawerReservedStockBatchDetail,
  },
  data() {
    return {
      activeTab: 'upload',
      isDownloading: false,
      isUploading: false,
      file: null,
      processId: null,
      processStatus: null,
      processResult: null,
      pollingInterval: null,

      // History
      historyData: [],
      isHistoryLoading: false,
      warehouseHash: {},
      searchText: '',
      drawerVisible: false,
      selectedBatch: null,
      expandedData: {},
      expandedLoading: {},
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
      },
    }
  },
  computed: {
    businessId() {
      if (this.restriction === 'Warehouse') {
        return Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
      }
      return this.$route.query.business_id || this.$store.state.user.businessList?.[0]?.business_id
    },
    workspaceId() {
      return this.$route.query.workspace_id || this.$store.state.user.workspace_id_active
    },
    restriction() {
      return this.$store.state.user.restriction_base
    },
    warehouseList() {
      // We already have warehouseHash which is derived from the same store dispatch
      return Object.values(this.warehouseHash).map(w => ({
        value: w.id || w.warehouse_id,
        label: w.name || w.warehouse_name,
      })).sort((a, b) => a.label.localeCompare(b.label))
    },
    warehouseListOptions() {
      return [{ value: undefined, label: this.$t('topBar.all_Warehouse') }, ...this.warehouseList]
    },
    warehouseId: {
      get() {
        return this.$route.query.warehouse_id
      },
      set(val) {
        this.updateQuery({ warehouse_id: val, page: 1 })
      },
    },
    historyColumns() {
      return [
        {
          title: 'Date',
          dataIndex: 'started_at',
          key: 'started_at',
          customRender: (text) => text ? dayjs(text).format('DD MMM YYYY HH:mm:ss') : '-',
        },
        {
          title: 'Total Rows',
          dataIndex: 'total_rows',
          align: 'right',
        },
        {
          title: 'Success',
          dataIndex: 'success_count',
          align: 'right',
          scopedSlots: { customRender: 'success_count' },
        },
        {
          title: 'Failed',
          dataIndex: 'failed_count',
          align: 'right',
          scopedSlots: { customRender: 'failed_count' },
        },
        {
          title: 'Status',
          dataIndex: 'status',
          align: 'center',
          scopedSlots: { customRender: 'batchStatus' },
        },
        {
          title: 'User',
          dataIndex: 'user_name',
          key: 'user_name',
        },
        {
          title: 'Action',
          key: 'action',
          align: 'center',
          scopedSlots: { customRender: 'action' },
        },
      ]
    },
    recordColumns() {
      return [
        {
          title: 'Date',
          dataIndex: 'created_at',
          key: 'created_at',
          width: 160,
          customRender: (text) => text ? dayjs(text).format('DD MMM YYYY HH:mm:ss') : '-',
        },
        {
          title: 'SKU',
          dataIndex: 'sku',
          key: 'sku',
          scopedSlots: { customRender: 'sku' },
        },
        {
          title: 'Product Title',
          dataIndex: 'title',
          key: 'title',
          scopedSlots: { customRender: 'title' },
        },
        {
          title: 'Prev Reserved',
          dataIndex: 'previous_reserved_stock',
          key: 'previous_reserved_stock',
          align: 'right',
          scopedSlots: { customRender: 'previous_reserved_stock' },
          customRender: (text) => text ?? '-',
        },
        {
          title: 'Value',
          key: 'reserved_stock',
          align: 'right',
          scopedSlots: { customRender: 'new_reserved_stock' },
          customRender: (record) => record.new_reserved_stock ?? record.reserved_stock ?? 0,
        },
        {
          title: 'Reason',
          dataIndex: 'reason',
          key: 'reason',
          ellipsis: true,
        },
        {
          title: 'Status/Msg',
          key: 'status_msg',
          customRender: (record) => record.message || record.status || '-',
        },
        {
          title: 'User',
          dataIndex: 'user_name',
          key: 'user_name',
        },
      ]
    },
    errorColumns() {
      return [
        { title: this.$t('reservedStock.error_table_row') || 'Row', dataIndex: 'row_number', width: 80 },
        { title: 'SKU', dataIndex: 'sku', width: 200 },
        { 
          title: this.$t('reservedStock.error_table_message') || 'Error Message', 
          dataIndex: 'error_message',
          scopedSlots: { customRender: 'errorMessage' },
        },
      ]
    },
  },
  watch: {
    businessId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchWarehouseHash()
        }
      },
    },
    activeTab(val) {
      if (val === 'history') {
        this.fetchHistory()
      }
    },
    '$route.query': {
      handler() {
        if (this.activeTab === 'history') {
          this.fetchHistory()
        }
      },
      deep: true,
    },
  },
  async created() {
    if (this.businessId) {
      this.fetchWarehouseHash()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async fetchWarehouseHash() {
      try {
        const { data } = await this.$store.dispatch('warehouse/GETSALESWAREHOUSE', {
          business_id: this.businessId,
          size: 999999,
        })
        
        this.warehouseHash = (data || []).reduce((acc, warehouse) => {
          const id = warehouse.id || warehouse.warehouse_id
          if (id) {
            acc[id] = {
              ...warehouse,
              warehouse_name: warehouse.name || warehouse.warehouse_name,
            }
          }
          return acc
        }, {})
      } catch (err) {
        console.error('Failed to fetch warehouse hash:', err)
      }
    },
    updateQuery(query = {}) {
      this.$router.push({
        query: {
          ...this.$route.query,
          ...query,
        },
      }).catch(() => {})
    },
    goBackToIndex() {
      this.$router.push({
        name: 'inventory.list.inventory.v2',
        query: this.$route.query,
      })
    },
    async downloadTemplate() {
      this.isDownloading = true
      try {
        const response = await downloadReservedStockTemplate({
          business_id: this.businessId,
          workspace_id: this.workspaceId,
        })
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'Template_Bulk_Reserved_Stock.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (e) {
        this.$notification.error({
          message: 'Error',
          description: this.$t('reservedStock.download_template_error'),
        })
      } finally {
        this.isDownloading = false
      }
    },
    beforeUpload(file) {
      this.file = file
      this.processResult = null
      this.processStatus = null
      return false
    },
    removeFile() {
      this.file = null
      this.processResult = null
      this.processStatus = null
    },
    async handleUpload() {
      if (!this.file) return

      this.isUploading = true
      const userStore = this.$store.state.user

      try {
        const { data } = await uploadReservedStock({
          file: this.file,
          businessId: this.businessId,
          userId: userStore.id || userStore.uuid || '',
          userName: userStore.firstName ? `${userStore.firstName} ${userStore.lastName || ''}`.trim() : (userStore.email || 'System User'),
        })

        if (data && data.process_id) {
          this.processId = data.process_id
          this.processStatus = data.status || 'ONPROGRESS'
          this.$message.success(this.$t('reservedStock.upload_success_processing'))
          this.startPolling()
        }
      } catch (e) {
        this.isUploading = false
        const errorData = e?.response?.data
        if (errorData?.errors?.length) {
          this.processResult = {
            ...errorData,
            status: 'FAILED',
          }
        }
        this.$notification.error({
          message: this.$t('reservedStock.upload_error_title'),
          description: errorData?.message || e?.message || this.$t('reservedStock.upload_error_failed'),
        })
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollingInterval = setInterval(this.checkStatus, 2000)
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    async checkStatus() {
      if (!this.processId) return
      try {
        const { data } = await checkUploadStatus(this.processId)
        this.processStatus = data?.status

        if (['COMPLETED', 'SUCCESS', 'FAILED'].includes(data?.status)) {
          this.stopPolling()
          this.isUploading = false
          this.processResult = data
          if (data?.status === 'FAILED') {
            this.$notification.error({ 
              message: this.$t('reservedStock.import_failed'), 
              description: data?.message || this.$t('reservedStock.import_process_errors'), 
            })
          } else {
            this.$notification.success({ 
              message: this.$t('reservedStock.import_successful'),
              description: this.$t('reservedStock.import_completed_success', { count: data.success_count }),
            })
          }
        }
      } catch (e) {
        this.stopPolling()
        this.isUploading = false
      }
    },
    async fetchHistory() {
      this.isHistoryLoading = true
      try {
        const { data } = await getReservedStockUploadHistory({
          business_id: this.businessId,
          warehouse_id: this.warehouseId,
          page: this.pagination.current - 1, // API usually 0-indexed if using Spring Data
          size: this.pagination.pageSize,
          search: this.searchText,
        })
        
        this.historyData = data.content || data.data || []
        this.pagination.total = data.totalElements ?? data.total_of_elements ?? data.totalRow ?? 0
      } catch (e) {
        console.error(e)
      } finally {
        this.isHistoryLoading = false
      }
    },
    onSearch() {
      this.pagination.current = 1
      this.fetchHistory()
    },
    highlightText(text) {
      if (!this.searchText || !text) return text
      const regex = new RegExp(`(${this.searchText.trim()})`, 'gi')
      return String(text).replace(regex, '<mark style="background-color: #fffb8f; padding: 0; border-radius: 2px; color: rgba(0, 0, 0, 0.85);">$1</mark>')
    },
    handleTableChangePagination(page) {
      this.pagination.current = page
      this.fetchHistory()
    },
    handleSizeChange(current, size) {
      this.pagination.current = 1
      this.pagination.pageSize = size
      this.fetchHistory()
    },
    openDetailDrawer(batch) {
      this.selectedBatch = batch
      this.drawerVisible = true
    },
    getStatusColor(status) {
      switch (status) {
        case 'SUCCESS':
        case 'COMPLETED': return 'green'
        case 'FAILED': return 'red'
        case 'ONPROGRESS': return 'blue'
        default: return 'gray'
      }
    },
    async downloadFile(processId) {
      if (!processId) return
      try {
        const response = await downloadAnnotatedResult(processId)
        const blob = new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        // 1. Try to get filename from content-disposition header (Server Choice)
        let filename = ''
        const disposition = response.headers['content-disposition']
        if (disposition && disposition.includes('filename=')) {
          filename = disposition.split('filename=')[1].replace(/['"]/g, '')
        }
        
        // 2. Fallback
        if (!filename) {
          filename = `Result_${processId}.xlsx`
        }

        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error('Download failed', e)
        this.$notification.error({ 
          message: 'Error', 
          description: this.$t('reservedStock.download_error') || 'Failed to download file.', 
        })
      }
    },
  },
}
</script>


<style lang="scss" scoped>

.pca-tab-store::v-deep {
  .ant-tabs-bar {
    background-color: #fff;
    margin-bottom: 0;
    border-bottom-color: transparent;
  }
  .ant-tabs-nav .ant-tabs-tab:hover,
  .ant-tabs-nav .ant-tabs-tab-active {
    color: #ff0a54;
  }
  .ant-tabs-ink-bar {
    background-color: #ff0a54;
  }

  .ant-tabs-large-bar .ant-tabs-nav-container {
    font-size: 14px;
  }

  .ant-tabs-large-bar .ant-tabs-tab {
    padding: 12px 14px;
  }
}

.card-no-padding {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  margin-top: 16px;
}

.history-table-wrapper {
  overflow-y: auto;
  height: calc(100vh - 344px);
  border: 1px solid #f0f0f0;
  background: #fff;
}

.history-list-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f0f2f4;
    padding: 14px 16px;
    font-size: 11px;
    font-weight: 500;
    color: #757575;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    text-align: left;
    border-bottom: 2px solid #f0f0f0;
    box-shadow: inset 0 -1px 0 #f0f0f0; /* Ensure border is visible when sticky */
  }
}
</style>
